import { useEffect, useState, forwardRef } from "react";
import { useEntityData } from "@/hooks/useEntityData";
import EntityGrid, { EntityGridProps } from "@/components/entity/EntityGrid";
import { SkeletonTableRows } from "@/components/ui/Skeleton";
import { Box, Text } from "@radix-ui/themes";
import { IAny } from "@/types/breeze/Any";

interface ManagedEntityGridProps extends Pick<
  EntityGridProps,
  | "rowSelection"
  | "onCellClicked"
  | "disableFieldSelector"
  | "onSelectionChanged"
  | "linkTemplate"
  | "additionalActions"
  | "onRowView"
> {
  entityName: string;
  label?: string;
  filterQuery?: string;
  selectedRecordIds?: number[];
}

const ManagedEntityGrid = forwardRef<HTMLDivElement, ManagedEntityGridProps>(
  (
    {
      entityName,
      label,
      filterQuery,
      rowSelection,
      disableFieldSelector,
      selectedRecordIds,
      additionalActions,
      onCellClicked,
      onSelectionChanged,
      linkTemplate,
      onRowView,
    },
    ref,
  ) => {
    // Track selected IDs separately from the grid's internal state
    const [selectedIds, setSelectedIds] = useState<number[]>(
      selectedRecordIds || [],
    );

    // Sync selectedIds with selectedRecordIds when it changes externally
    useEffect(() => {
      if (selectedRecordIds) {
        setSelectedIds(selectedRecordIds);
      }
    }, [selectedRecordIds]);

    const {
      entityDefinition,
      entityTree,
      entityData,
      isLoading,
      error,
      selectedFields,
      page,
      pageSize,
      setSelectedFields,
      setPage,
      setPageSize,
      setFilterQuery,
    } = useEntityData(entityName, { disableFieldSelector });

    useEffect(() => {
      if (filterQuery) {
        setFilterQuery(filterQuery);
      }
    }, [filterQuery, setFilterQuery]);

    // Handle selection changes from the grid
    const handleSelectionChanged = (selectedRows: IAny[]) => {
      // Get IDs from the currently selected rows
      const currentPageSelectedIds = selectedRows.map((row) => row.id);

      // Create a new Set of all selected IDs
      const allSelectedIds = new Set(selectedIds);

      // For each ID in the current page data
      entityData?.items.forEach((item) => {
        const id = item.id;
        // If it's in the current selection, add it
        if (currentPageSelectedIds.includes(id)) {
          allSelectedIds.add(id);
        } else {
          // If it's not in the current selection, remove it
          allSelectedIds.delete(id);
        }
      });

      const newSelectedIds = Array.from(allSelectedIds);
      setSelectedIds(newSelectedIds);

      // Convert IDs back to IAny objects for the callback
      const selectedObjects = newSelectedIds.map((id) => ({
        id,
        locked: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }));
      onSelectionChanged?.(selectedObjects);
    };

    if (isLoading) return <Box p="4"><SkeletonTableRows rows={8} columns={5} /></Box>;

    if (error || !entityDefinition || !entityTree || !entityData)
      return <div>There was an error loading the entity data.</div>;

    return (
      <div ref={ref}>
        <Box mb={"2"}>
          {label && (
            <div className="flex items-center justify-between">
              <Text as="label" size="2" weight="medium" mb="1">
                {label}
              </Text>
            </div>
          )}
        </Box>
        <EntityGrid
          entityDefinition={entityDefinition}
          entityData={entityData}
          currentPage={page}
          currentPageSize={pageSize}
          isLoading={isLoading}
          selectedFields={selectedFields}
          entityTree={entityTree}
          rowSelection={rowSelection}
          disableFieldSelector={disableFieldSelector}
          selectedRecordIds={selectedIds}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onFilterChange={setFilterQuery}
          onSelectionChanged={handleSelectionChanged}
          onFieldsChanged={setSelectedFields}
          onCellClicked={onCellClicked}
          linkTemplate={linkTemplate}
          additionalActions={additionalActions}
          onRowView={onRowView}
        />
      </div>
    );
  },
);

ManagedEntityGrid.displayName = "ManagedEntityGrid";

export default ManagedEntityGrid;
